import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import SmartImage from 'dpl/components/SmartImage';
import Icon from 'dpl/common/components/Icon';

export function TipsSliderButton({
  direction,
  disabled,
  isAnimated,
  onAnimationIteration,
  onClick,
  onMouseEnter,
  onMouseLeave
}) {
  return (
    <button
      className="TellYourTailTipsSliderButton br-100 relative"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      type="button"
    >
      {isAnimated && (
        <svg className="TellYourTailTipsSliderButton__svg absolute top-0 left-0">
          <circle
            className="TellYourTailTipsSliderButton__circle TellYourTailTipsSliderButton__circle--animating"
            r="19"
            cx="20"
            cy="20"
            onAnimationIteration={onAnimationIteration}
          />
        </svg>
      )}
      <Icon
        className="transform-center-all purple"
        direction={direction}
        name={`fat-arrow-${direction}`}
      />
    </button>
  );
}

TipsSliderButton.propTypes = {
  direction: PropTypes.oneOf(['left', 'right']).isRequired,
  disabled: PropTypes.bool,
  isAnimated: PropTypes.bool,
  onAnimationIteration: PropTypes.func,
  onClick: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};

TipsSliderButton.defaultProps = {
  disabled: false,
  isAnimated: false,
  onAnimationIteration: null,
  onMouseEnter: null,
  onMouseLeave: null
};

export default class TipsSlider extends Component {
  static propTypes = {
    isInViewport: PropTypes.bool.isRequired,
    setLazyRef: PropTypes.func.isRequired,
    tips: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string.isRequired,
        image: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
      })
    ).isRequired
  };

  state = {
    currentIdx: 0,
    isAnimating: false,
    isDirty: false,
    isTransitioning: false
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isInViewport && this.props.isInViewport) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isAnimating: true });
    }
  }

  goBack = () =>
    this.setState({
      currentIdx: this.state.currentIdx - 1,
      isAnimating: false,
      isDirty: true,
      isTransitioning: true
    });

  goForward = () =>
    this.setState({
      currentIdx: this.state.currentIdx + 1,
      isTransitioning: true
    });

  handleTransitionEnd = () => {
    this.setState({
      isTransitioning: false
    });
  };

  handleMouseButtonEnter = () =>
    this.setState({
      isDirty: true,
      isAnimating: false
    });

  handleMouseButtonLeave = () => this.setState({ isAnimating: true });

  handleAnimationIteration = () => {
    this.goForward();
  };

  render() {
    const { setLazyRef, tips } = this.props;

    const { currentIdx, isAnimating, isDirty, isTransitioning } = this.state;

    return (
      <div
        className={classnames('TellYourTailTipsSlider', {
          'TellYourTailTipsSlider--animating': isAnimating && !isDirty
        })}
        ref={setLazyRef}
      >
        <div className="row">
          <div className="col-6">
            <div className="overflow-hidden">
              <ul
                className="TellYourTailTipsSlider__images flex"
                onTransitionEnd={this.handleTransitionEnd}
                style={{
                  transform: `translate3d(${
                    -100 * this.state.currentIdx
                  }%, 0, 0)`
                }}
              >
                {tips.map(({ image }, idx) => (
                  <li className="TellYourTailTipsSlider__image" key={idx}>
                    <SmartImage alt={`tip ${idx + 1}`} src={image} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-6">
            <div className="TellYourTailTipsSlider__content">
              <h6 className="pt10 primary">
                10 really important things you should know when getting a dog
              </h6>
              <div className="overflow-hidden mb4">
                <ul
                  className="TellYourTailTipsSlider__texts flex"
                  onTransitionEnd={this.handleTransitionEnd}
                  style={{
                    transform: `translate3d(${
                      -100 * this.state.currentIdx
                    }%, 0, 0)`
                  }}
                >
                  {tips.map(({ title, content }, idx) => (
                    <li className="TellYourTailTipsSlider__text" key={idx}>
                      <p className="pv10 fw-medium f4-xl">{idx + 1}/10</p>
                      <p className="TellYourTailTipsSlider__title primary mb2 mb4-xl fw-medium">
                        {title}
                      </p>
                      {/* eslint-disable-next-line react/no-danger */}
                      <p dangerouslySetInnerHTML={{ __html: content }} />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex">
                <div className="mh2">
                  <TipsSliderButton
                    direction="left"
                    disabled={isTransitioning || currentIdx === 0}
                    onClick={this.goBack}
                    onMouseEnter={this.handleMouseButtonEnter}
                    onMouseLeave={this.handleMouseButtonLeave}
                  />
                </div>
                <div className="mh2">
                  <TipsSliderButton
                    isAnimated={!isDirty && isAnimating && currentIdx < 9}
                    direction="right"
                    disabled={isTransitioning || currentIdx === 9}
                    onAnimationIteration={this.handleAnimationIteration}
                    onClick={this.goForward}
                    onMouseEnter={this.handleMouseButtonEnter}
                    onMouseLeave={this.handleMouseButtonLeave}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
